import usersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import AuthenticatedController from '@mm/state/controllers/AuthenticatedController';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

// Note: We load and update all userAttributes only when the user logs in.
// Then we only update pieces of the userAttributes, with the assumption
// that we know all the changes.
// It was fine before we introduced Mobile that is also actively manipulating
// userAttributes.
// This hook allows an easy way to refresh userAttributes, minimizing potential conflicts.
// We might think of introducing polling later, but for now it is one-time only.

export const useFreshUserAttributes = () => {
  const companyId = useSelector(selectActiveCompanyId);
  const oldSession = useSelector((state: StoreState) => state.session.session);
  const { id: userId, userAttributes: currentUserAttributes } =
    oldSession || {};
  const dispatch: Dispatch<StoreState> = useDispatch();

  const [areUserAttributesUptodate, setAreUserAttributesUptodate] =
    useState(false);
  useEffect(() => {
    if (!userId) {
      return;
    }
    const getFreshUserData = async () => {
      const response = await dispatch(
        usersCRUDActions.retrieveById(companyId, userId),
      );
      const { userAttributes } = response;

      if (!isEqual(userAttributes, currentUserAttributes)) {
        await AuthenticatedController.storeSession({
          ...oldSession,
          userAttributes,
        });
        dispatch({
          type: usersCRUDActions.updateUserAttributesSessionAction,
          payload: { userAttributes },
        });
      }
      setAreUserAttributesUptodate(true);
    };
    getFreshUserData();
  }, [userId]);

  return { areUserAttributesUptodate };
};
